import React, { useRef, useEffect, FC } from 'react';
import { View, Text, StyleSheet, ScrollView, Animated } from 'react-native';
import i18n from 'i18n-js';

import Svg from '@styles/icons/svg';

import { isPhone, isWeb } from '@helpers';
import { width, scale, height } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import ColorWrapper from '@styles/colors';
import { FontStyles, font as initialFont } from '@styles/fonts';
import { WithColorProps } from '@styles/colors/types';
import { connect } from 'react-redux';
import { setModalMissingPIOpen } from '@reducers/machinesV2';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

interface ActionModalProps extends WithColorProps {
  setModalMissingPIOpen: (value: boolean) => void;
  CC?: any;
  modalMissingPIOpen: boolean;
  font?: FontStyles;
}

const ActionsModal: FC<ActionModalProps> = ({ setModalMissingPIOpen, C, CC, modalMissingPIOpen, font = initialFont }) => {
  const s = scale;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const CCC = CC || C;

  useEffect(() => {
    if (modalMissingPIOpen) {
      fadeIn();
    } else {
      fadeOut();
    }
  }, [modalMissingPIOpen]);

  const duration = 300;

  const fadeIn = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: duration,
      useNativeDriver: false,
    }).start();
  };

  const fadeOut = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: duration,
      useNativeDriver: false,
    }).start();
  };

  return (
    <Animated.View
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: CCC('Overlay/Dark/Dim strong'),
        zIndex: fadeAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [-200, 10000],
        }),
        opacity: fadeAnim.interpolate({
          inputRange: [0, 0.5, 1],
          outputRange: [0, 1, 1],
        }),
      }}
    >
      <View
        style={{
          width: isWeb ? width : '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          alignContent: 'center',
        }}
      >
        <Animated.View
          style={[
            isPhone
              ? {
                  backgroundColor: CCC('Background / Dark / Modal'),
                  borderTopRightRadius: s(SCREEN_BORDER_RADIUS),
                  borderTopLeftRadius: s(SCREEN_BORDER_RADIUS),
                  top: s(53),
                  bottom: 0,
                  width: '100%',
                }
              : {
                  backgroundColor: CCC('Background / Dark / Modal'),
                  borderRadius: s(SCREEN_BORDER_RADIUS),
                  left: s(100),
                  right: s(100),
                  top: s(164),
                  paddingBottom: s(21),
                },
            {
              position: 'absolute',
              marginTop: fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [height, 0],
              }),
            },
          ]}
        >
          <View style={{ width: s(27), height: s(27), top: 23, right: 18, position: 'absolute', zIndex: 1000 }}>
            <TouchableElement onPress={() => setModalMissingPIOpen(false)} style={{ width: '100%', height: '100%' }}>
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      backgroundColor: CCC(pressHoverColor('Button/Dark/Tertiary')),
                      width: s(27),
                      height: s(27),
                      borderRadius: s(27) / 2,
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Svg icon="times" width={s(11)} height={s(11)} color="Button/Dark/Secondary Text" key={s(11)} />
                </View>
              )}
            </TouchableElement>
          </View>
          <View
            style={{
              borderBottomColor: CCC('Hairline / Dark / No trans'),
              width: '100%',
              height: s(72),
              justifyContent: 'center',
              alignContent: 'center',
            }}
          >
            <Text
              style={[
                { color: CCC('Label / Dark / Primary'), paddingLeft: s(isPhone ? 18 : 0), textAlign: isPhone ? 'left' : 'center' },
                font[isPhone ? 'Headline' : 'Title 2'],
              ]}
            >
              {i18n.t('Production Information Missing')}
            </Text>
            <View
              style={{
                width: '100%',
                position: 'absolute',
                bottom: 0,
                paddingRight: s(isPhone ? 0 : 18),
                paddingLeft: s(isPhone ? 0 : 18),
              }}
            >
              <View style={{ width: '100%', borderBottomColor: CCC('Hairline/Dark/No trans'), borderBottomWidth: StyleSheet.hairlineWidth }} />
            </View>
          </View>
          <ScrollView>
            <View style={{ flexDirection: isPhone ? 'column' : 'row', width: '100%' }}>
              <View
                style={[
                  isPhone
                    ? {
                        width: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingLeft: s(18),
                        paddingRight: s(18),
                        paddingBottom: s(27),
                      }
                    : {
                        borderRightWidth: StyleSheet.hairlineWidth,
                        borderRightColor: CCC('Hairline/Dark/No trans'),
                        paddingLeft: s(27),
                        paddingRight: s(27),
                        alignItems: 'center',
                        width: '50%',
                      },
                ]}
              >
                <View style={{ width: s(153), height: s(72), marginTop: s(38) }}>
                  <TouchableElement style={{ width: '100%', height: '100%' }}>
                    {({ boxStyle, pressHoverColor }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            backgroundColor: CCC(pressHoverColor('Button/Dark/Primary')),
                            borderRadius: s(10),
                            width: '100%',
                            height: '100%',
                          },
                        ]}
                      >
                        <View style={{ width: '100%', top: s(19), justifyContent: 'center', alignItems: 'center', position: 'absolute' }}>
                          <Svg icon="machineLink" width={s(27)} height={s(14)} color="Button/Dark/Primary Text" key={s(27)} />
                        </View>
                        <Text
                          style={[
                            { color: CCC('Button/Dark/Primary Text'), width: '100%', textAlign: 'center', top: s(45), position: 'absolute' },
                            font['Button 1'],
                          ]}
                        >
                          {i18n.t('Add')}
                        </Text>
                      </View>
                    )}
                  </TouchableElement>
                </View>
                <Text style={[{ color: CCC('Label / Dark / Primary'), marginTop: s(28) }, font['Subhead 1']]}>
                  {i18n.t('Add Production Information to an NC File like setup time, runtime, drawings')}
                </Text>
                {isPhone && (
                  <View
                    style={{
                      borderBottomWidth: StyleSheet.hairlineWidth,
                      borderBottomColor: CCC('Hairline/Dark/No trans'),
                      width: '100%',
                      position: 'absolute',
                      bottom: 0,
                    }}
                  />
                )}
              </View>
              <View
                style={[
                  isPhone
                    ? {
                        width: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingLeft: s(18),
                        paddingRight: s(18),
                        paddingBottom: s(27),
                      }
                    : {
                        paddingLeft: s(27),
                        paddingRight: s(27),
                        alignItems: 'center',
                        width: '50%',
                      },
                ]}
              >
                <View style={{ width: s(153), height: s(72), marginTop: s(38) }}>
                  <TouchableElement style={{ width: '100%', height: '100%' }}>
                    {({ boxStyle, pressHoverColor }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            backgroundColor: CCC(pressHoverColor('Button/Dark/Secondary')),
                            borderRadius: s(10),
                            width: '100%',
                            height: '100%',
                          },
                        ]}
                      >
                        <View style={{ width: '100%', top: s(13), justifyContent: 'center', alignItems: 'center', position: 'absolute' }}>
                          <Svg icon="machineIgnored" width={s(25)} height={s(25)} color="Button/Dark/Secondary Text" key={s(25)} />
                        </View>
                        <Text
                          style={[
                            { color: CCC('Button/Dark/Secondary Text'), width: '100%', textAlign: 'center', top: s(45), position: 'absolute' },
                            font['Button 1'],
                          ]}
                        >
                          {i18n.t('Ignore')}
                        </Text>
                      </View>
                    )}
                  </TouchableElement>
                </View>
                <View style={{ width: '100%' }}>
                  <Text style={[{ color: CCC('Label / Dark / Primary'), marginTop: s(28) }, font['Subhead 1']]}>
                    {i18n.t(
                      "Select Ignore if you don't want to add Production Information to this NC File Starts and Stops of this NC File will be ignored and will not change the status of a machine"
                    )}
                  </Text>
                  <Text style={[{ color: CCC('Label / Dark / Primary'), marginTop: s(18) }, font.Headline]}>
                    {i18n.t('Examples of NC Files that should be ignored:')}
                  </Text>
                  <Text style={[{ color: CCC('Label / Dark / Primary'), marginTop: s(18) }, font['Subhead 2']]}>{i18n.t('Subprogram')}</Text>
                  <Text style={[{ color: CCC('Label / Dark / Primary'), marginTop: s(18) }, font['Subhead 1']]}>{i18n.t('Shell program')}</Text>
                  <Text style={[{ color: CCC('Label/Dark/Secondary') }, font['Subhead 2']]}>{i18n.t('Sometime used for automation')}</Text>
                </View>
              </View>
            </View>
          </ScrollView>
        </Animated.View>
      </View>
    </Animated.View>
  );
};

const mapStateToProps = ({ machinesV2: { modalMissingPIOpen } }) => ({ modalMissingPIOpen });

export default connect(mapStateToProps, { setModalMissingPIOpen })(ColorWrapper(ActionsModal));
